import { supabase } from './supabaseService';

const GRAPH_API_BASE = 'https://graph.microsoft.com/v1.0';

/**
 * Graph `/me/events` with `start.timeZone: Europe/Amsterdam` expects `dateTime` as **wall clock**
 * in that zone (`YYYY-MM-DDTHH:mm:ss`) **without** `Z` or `±hh:mm`. Passing UTC ISO (`...Z`) makes
 * Outlook apply the TZ again → appointments ~1–2h off in NL summer/winter.
 */
function eventDateTimeForGraphEuropeAmsterdam(isoOrWall: string): string {
  const s = isoOrWall.trim();
  const naiveWall = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;
  if (naiveWall.test(s) && !/Z$/i.test(s) && !/[+-]\d{2}:?\d{2}$/.test(s)) {
    return s.length === 16 ? `${s}:00` : s;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .format(d)
    .replace(' ', 'T');
}

/**
 * Helper to get the current Microsoft provider token from the active session.
 */
const getProviderToken = async (): Promise<string | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session || !session.provider_token) {
        // console.warn("No provider token found in session. User might not have logged in with Microsoft, or token expired.");
        return null;
    }
    return session.provider_token;
};

export type MicrosoftMailSessionCheckResult =
    | { ok: true }
    | { ok: false; code: 'no_session' | 'no_token' | 'unauthorized' | 'graph_error'; message: string };

async function graphMeProbe(accessToken: string): Promise<Response> {
    return fetch(`${GRAPH_API_BASE}/me?$select=id`, {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
}

/**
 * Proactively checks whether the current user can call Microsoft Graph for mail
 * (token present + accepted by Graph).
 *
 * Important: do **not** call {@link supabase.auth.refreshSession} before reading
 * `session.provider_token`. GoTrue’s refresh response often omits `provider_token`, so
 * refreshing first makes a fresh Microsoft login look “tokenless” until full sign-out.
 * We probe Graph first; only on 401/403 we refresh and retry (keeping the previous token
 * if the refreshed session still omits it).
 */
export async function verifyMicrosoftGraphMailSession(): Promise<MicrosoftMailSessionCheckResult> {
    const { data: { session: initial }, error: sessionErr } = await supabase.auth.getSession();
    if (sessionErr || !initial?.user) {
        return {
            ok: false,
            code: 'no_session',
            message: 'Je bent niet ingelogd. Log opnieuw in en gebruik waar mogelijk “Inloggen met Microsoft”.',
        };
    }

    let token: string | null = initial.provider_token ?? null;

    const noTokenMsg: MicrosoftMailSessionCheckResult = {
        ok: false,
        code: 'no_token',
        message:
            'Er is geen Microsoft 365-mailtoken in je sessie. Log volledig uit en meld je opnieuw aan met «Inloggen met Microsoft» (niet met alleen e-mail en wachtwoord). Zo geeft Werftools toestemming om mail via Outlook te versturen.',
    };

    const interpretResponse = async (response: Response): Promise<MicrosoftMailSessionCheckResult> => {
        if (response.ok) return { ok: true };
        if (response.status === 401 || response.status === 403) {
            return {
                ok: false,
                code: 'unauthorized',
                message:
                    'Microsoft 365 accepteert je sessie niet meer (meestal verlopen na lang inactief). Log opnieuw in met Microsoft voordat je mailt — dan hoef je je concept niet dubbel te doen.',
            };
        }
        let detail = '';
        try {
            const errJson = await response.json();
            detail = errJson?.error?.message ? ` (${errJson.error.message})` : '';
        } catch {
            /* ignore */
        }
        return {
            ok: false,
            code: 'graph_error',
            message: `Microsoft 365 kon niet worden bereikt (${response.status})${detail}. Probeer het zo opnieuw of log opnieuw in met Microsoft.`,
        };
    };

    try {
        if (!token) {
            try {
                await supabase.auth.refreshSession();
            } catch {
                /* ignore */
            }
            const { data: { session: afterRefresh } } = await supabase.auth.getSession();
            token = afterRefresh?.provider_token ?? null;
            if (!token) return noTokenMsg;
        }

        let response = await graphMeProbe(token);
        if (response.ok) return { ok: true };

        if (response.status === 401 || response.status === 403) {
            const preserved = token;
            try {
                await supabase.auth.refreshSession();
            } catch {
                /* ignore */
            }
            const { data: { session: s2 } } = await supabase.auth.getSession();
            token = s2?.provider_token || preserved;
            response = await graphMeProbe(token);
        }

        return await interpretResponse(response);
    } catch {
        return {
            ok: false,
            code: 'graph_error',
            message: 'Netwerkfout bij de controle met Microsoft. Controleer je verbinding en probeer het opnieuw.',
        };
    }
}

/**
 * Creates a Microsoft Teams meeting and adds it to the user's Outlook calendar.
 * 
 * @param subject The title of the interview/meeting.
 * @param startDateTime ISO string for start time.
 * @param endDateTime ISO string for end time.
 * @param attendees Array of email addresses.
 * @returns The Join Web URL (Teams link) if successful.
 */
export interface CreateTeamsMeetingOptions {
    bodyContent?: string;
    isOnlineMeeting?: boolean;
    recordAutomatically?: boolean;
    lobbyBypass?: string; // e.g. 'everyone', 'organization', 'organizationAndGuests'
    locationName?: string;
}

/**
 * Creates a Microsoft Teams meeting and adds it to the user's Outlook calendar.
 *
 * @param subject The title of the interview/meeting.
 * @param startDateTime Start time as UTC ISO (`...Z`) from `Date.toISOString()`, or Amsterdam wall `YYYY-MM-DDTHH:mm:ss` without zone.
 * @param endDateTime End time (same formats as start).
 * @param attendees Array of email addresses.
 * @param options Advanced meeting options.
 * @returns The Join Web URL (Teams link) if successful.
 */
export const createTeamsMeeting = async (
    subject: string,
    startDateTime: string,
    endDateTime: string,
    attendees: { email: string, name?: string }[],
    options: CreateTeamsMeetingOptions = {}
): Promise<{ joinUrl: string | null, error: any }> => {
    const token = await getProviderToken();

    if (!token) {
        return { joinUrl: null, error: new Error('Microsoft authentication required. Please log out and back in with Microsoft.') };
    }

    try {
        const graphStart = eventDateTimeForGraphEuropeAmsterdam(startDateTime);
        const graphEnd = eventDateTimeForGraphEuropeAmsterdam(endDateTime);

        const payload: any = {
            subject: subject,
            body: {
                contentType: 'HTML',
                content: options.bodyContent || 'Beste sollicitant,<br><br>Hierbij de uitnodiging voor ons online interview. De Teams link is bijgevoegd.<br><br>Met vriendelijke groet,<br>Werftools'
            },
            start: {
                dateTime: graphStart,
                timeZone: 'Europe/Amsterdam'
            },
            end: {
                dateTime: graphEnd,
                timeZone: 'Europe/Amsterdam'
            },
            location: {
                displayName: options.isOnlineMeeting !== false ? 'Microsoft Teams Meeting' : (options.locationName || 'Unspecified location')
            },
            attendees: attendees.map(a => ({
                emailAddress: {
                    address: a.email,
                    name: a.name || a.email
                },
                type: 'required'
            })),
            isOnlineMeeting: options.isOnlineMeeting !== false
        };

        if (payload.isOnlineMeeting) {
            // payload.onlineMeetingProvider = 'teamsForBusiness';
            // The /me/events endpoint does not support mutating complex onlineMeeting properties
            // like lobbyBypassSettings or recordAutomatically upon creation. It only supports the provider flag.
        }

        const response = await fetch(`${GRAPH_API_BASE}/me/events`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Prefer': 'outlook.timezone="Europe/Amsterdam"'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json();
            console.error("Microsoft Graph Error response:", errorData);
            throw new Error(errorData.error?.message || 'Failed to create meeting');
        }

        const data = await response.json();
        const joinUrl = data.onlineMeeting?.joinUrl;

        return { joinUrl, error: null };

    } catch (err) {
        console.error("Error calling Microsoft Graph API:", err);
        return { joinUrl: null, error: err };
    }
};

/**
 * Creates a draft email message in the user's Outlook Drafts folder.
 * 
 * @param subject Subject of the email
 * @param content HTML body of the email
 * @param toEmail Recipient email address
 * @param ccEmails Optional array of CC email addresses
 * @param bccEmails Optional array of BCC email addresses
 * @returns Object with success status or error
 */
export const createDraftMessage = async (
    subject: string,
    content: string,
    toEmail: string,
    ccEmails: string[] = [],
    bccEmails: string[] = [],
    fromEmail?: string
): Promise<{ success: boolean, error: any }> => {
    const token = await getProviderToken();
    if (!token) return { success: false, error: new Error('Microsoft authentication required.') };

    try {
        const payload: any = {
            subject: subject,
            body: {
                contentType: 'HTML',
                content: content
            },
            toRecipients: [
                {
                    emailAddress: {
                        address: toEmail
                    }
                }
            ],
            ...(fromEmail ? { from: { emailAddress: { address: fromEmail } } } : {})
        };

        if (ccEmails && ccEmails.length > 0) {
            payload.ccRecipients = ccEmails.filter(e => e.trim()).map(address => ({ emailAddress: { address: address.trim() } }));
        }
        if (bccEmails && bccEmails.length > 0) {
            payload.bccRecipients = bccEmails.filter(e => e.trim()).map(address => ({ emailAddress: { address: address.trim() } }));
        }

        const response = await fetch(`${GRAPH_API_BASE}/me/messages`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json();
            console.error("Microsoft Graph Draft Error:", errorData);
            throw new Error(errorData.error?.message || 'Failed to create draft message');
        }

        return { success: true, error: null };
    } catch (err) {
        console.error("Error creating draft via Microsoft Graph:", err);
        return { success: false, error: err };
    }
};

/**
 * Sends an email message directly via the user's Outlook account.
 * 
 * @param subject Subject of the email
 * @param content HTML body of the email
 * @param toEmail Recipient email address
 * @param ccEmails Optional array of CC email addresses
 * @param bccEmails Optional array of BCC email addresses
 * @returns Object with success status or error
 */
export const sendDirectMessage = async (
    subject: string,
    content: string,
    toEmail: string,
    ccEmails: string[] = [],
    bccEmails: string[] = [],
    fromEmail?: string
): Promise<{ success: boolean, error: any }> => {
    const token = await getProviderToken();
    if (!token) return { success: false, error: new Error('Microsoft authentication required.') };

    try {
        const payload: any = {
            message: {
                subject: subject,
                body: {
                    contentType: 'HTML',
                    content: content
                },
                toRecipients: [
                    {
                        emailAddress: {
                            address: toEmail
                        }
                    }
                ],
                ...(fromEmail ? { from: { emailAddress: { address: fromEmail } } } : {})
            },
            saveToSentItems: 'true'
        };

        if (ccEmails && ccEmails.length > 0) {
            payload.message.ccRecipients = ccEmails.filter(e => e.trim()).map(address => ({ emailAddress: { address: address.trim() } }));
        }
        if (bccEmails && bccEmails.length > 0) {
            payload.message.bccRecipients = bccEmails.filter(e => e.trim()).map(address => ({ emailAddress: { address: address.trim() } }));
        }

        const response = await fetch(`${GRAPH_API_BASE}/me/sendMail`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error("Microsoft Graph SendMail Error:", errorData);
            throw new Error(errorData.error?.message || 'Failed to send message directly');
        }

        return { success: true, error: null };
    } catch (err) {
        console.error("Error sending message via Microsoft Graph:", err);
        return { success: false, error: err };
    }
};

/**
 * Retrieves the user's M365 Mailbox signature.
 * Note: Requires an Exchange Online mailbox and potentially MailboxSettings.Read scope if strict.
 */
export const getMailboxSignature = async (): Promise<string | null> => {
    const token = await getProviderToken();
    if (!token) return null;

    try {
        const response = await fetch(`${GRAPH_API_BASE}/me/mailboxSettings`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (response.ok) {
            const data = await response.json();
            if (data.signature && data.signature.content) {
                return data.signature.content;
            }
        }
        return null;
    } catch {
        return null;
    }
};

/**
 * Retrieves the contents of the user's root OneDrive directory.
 * Useful for finding template documents.
 */
export const getOneDriveFiles = async () => {
    const token = await getProviderToken();
    if (!token) throw new Error('Microsoft authentication required.');

    try {
        const response = await fetch(`${GRAPH_API_BASE}/me/drive/root/children`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Failed to fetch OneDrive files');

        return await response.json();
    } catch (err) {
        console.error("Error fetching files:", err);
        throw err;
    }
};

/**
 * Retrieves the user's calendar events within a specified timeframe.
 * @param startDateTime ISO string for the start of the view window (default: today).
 * @param endDateTime ISO string for the end of the view window (default: +7 days).
 */
export const getCalendarEvents = async (
    startDateTime?: string,
    endDateTime?: string
): Promise<{ events: any[], error: any }> => {
    const token = await getProviderToken();
    if (!token) return { events: [], error: new Error('Microsoft authentication required.') };

    try {
        const start = startDateTime || new Date().toISOString();
        const end = endDateTime || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

        // We use calendarView to handle recurring events correctly
        const url = new URL(`${GRAPH_API_BASE}/me/calendarview`);
        url.searchParams.append('startDateTime', start);
        url.searchParams.append('endDateTime', end);
        // Only get the fields we actually need
        url.searchParams.append('$select', 'subject,start,end,location,onlineMeeting,webLink,attendees,isOnlineMeeting');
        url.searchParams.append('$orderby', 'start/dateTime');
        url.searchParams.append('$top', '50'); // Limit to 50 upcoming events

        const response = await fetch(url.toString(), {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Prefer': 'outlook.timezone="Europe/Amsterdam"'
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            console.error("Microsoft Graph Error response:", errorData);
            throw new Error(errorData.error?.message || 'Failed to fetch calendar events');
        }

        const data = await response.json();
        return { events: data.value || [], error: null };
    } catch (err) {
        console.error("Error calling Microsoft Graph API:", err);
        return { events: [], error: err };
    }
};

/**
 * Uploads a file buffer/blob to the user's OneDrive in a specific folder.
 * 
 * @param fileBlob The Blob or Buffer containing the file data
 * @param fileName The name of the file to save (e.g., 'CV_Jan_Jansen.docx')
 * @param folderPath The path in OneDrive to save it to (default: 'Kandidaten CVs')
 * @returns The webUrl (link to open in Word Online), drive item id (for later download/sync), and any potential errors
 */
export const uploadToOneDrive = async (
    fileBlob: Blob | Buffer,
    fileName: string,
    folderPath: string = 'Kandidaten CVs',
    contentType: string = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
): Promise<{ webUrl: string | null; itemId: string | null; error: any }> => {
    const token = await getProviderToken();
    if (!token) return { webUrl: null, itemId: null, error: new Error('Microsoft authentication required.') };

    try {
        // We use the simple upload API for files < 4MB. 
        // Endpoint: PUT /me/drive/root:/{folderPath}/{fileName}:/content
        const encodedPath = encodeURIComponent(folderPath);
        const encodedFile = encodeURIComponent(fileName);
        const url = `${GRAPH_API_BASE}/me/drive/root:/${encodedPath}/${encodedFile}:/content`;

        const response = await fetch(url, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': contentType
            },
            body: fileBlob as any
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error("OneDrive Upload Error response:", errorData);
            throw new Error(`Microsoft Fout: ${errorData.error?.message || errorData.message || response.statusText || 'Upload failed'}`);
        }

        const data = await response.json();
        return { webUrl: data.webUrl ?? null, itemId: data.id ?? null, error: null };
    } catch (err) {
        console.error("Error uploading to OneDrive via MS Graph API:", err);
        return { webUrl: null, itemId: null, error: err };
    }
};

/**
 * Downloads the current file bytes from OneDrive (same item as returned from {@link uploadToOneDrive}).
 */
export const downloadOneDriveFileByItemId = async (
    itemId: string
): Promise<{ blob: Blob | null; error: any }> => {
    const token = await getProviderToken();
    if (!token) return { blob: null, error: new Error('Microsoft authentication required.') };

    try {
        const url = `${GRAPH_API_BASE}/me/drive/items/${encodeURIComponent(itemId)}/content`;
        const response = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error?.message || response.statusText || 'Download failed');
        }

        const blob = await response.blob();
        return { blob, error: null };
    } catch (err) {
        console.error("Error downloading from OneDrive:", err);
        return { blob: null, error: err };
    }
};

/**
 * Retrieves the web URL for a specific folder in the user's OneDrive to open it in the browser.
 * 
 * @param folderPath The path in OneDrive to get the URL for (default: 'Kandidaten CVs')
 * @returns The webUrl (link to open in OneDrive) or null if not found/error
 */
export const getOneDriveFolderUrl = async (folderPath: string = 'Kandidaten CVs'): Promise<string | null> => {
    const token = await getProviderToken();
    if (!token) return null;

    try {
        const encodedPath = encodeURIComponent(folderPath);
        const url = `${GRAPH_API_BASE}/me/drive/root:/${encodedPath}`;

        let response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        // Auto-create folder if it doesn't exist
        if (response.status === 404) {
            console.log(`Folder '${folderPath}' not found, creating it...`);
            const createUrl = `${GRAPH_API_BASE}/me/drive/root/children`;
            response = await fetch(createUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: folderPath,
                    folder: {},
                    '@microsoft.graph.conflictBehavior': 'rename'
                })
            });
        }

        if (!response.ok) {
            console.error("Failed to get or create OneDrive folder info");
            return null;
        }

        const data = await response.json();
        return data.webUrl || null;
    } catch (err) {
        console.error("Error fetching OneDrive folder URL:", err);
        return null;
    }
};

// ==========================================
// SYSTEM-LEVEL GRAPH API (BACKEND PROXY)
// Used when the app needs to read Werfplaats staff calendars 
// (e.g., Client wants to book a meeting with Thom de Vries)
// ==========================================

const API_BASE = window.location.hostname === 'localhost' 
    ? 'http://localhost:4000/api/calendar' 
    : '/api/calendar';

export interface ScheduleResponse {
    scheduleId: string;
    availabilityView: string; // e.g., "0020002" where 0=free, 2=busy, 3=outOfOffice
    scheduleItems: {
        status: string;
        start: { dateTime: string, timeZone: string };
        end: { dateTime: string, timeZone: string };
    }[];
}

/**
 * Fetch Free/Busy schedules for one or more users between startTime and endTime via Backend Proxy.
 */
export async function getCalendarSchedule(emails: string[], startTime: string, endTime: string): Promise<ScheduleResponse[]> {
    const response = await fetch(`${API_BASE}/getSchedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails, startTime, endTime })
    });

    if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.details || errData.error || 'Failed to fetch schedule');
    }

    const data = await response.json();
    return data.schedules;
}

/**
 * Creates a Microsoft Teams meeting in the organizer's calendar via Backend Proxy.
 */
export async function createBackendTeamsMeeting(
    organizerEmail: string,
    candidateName: string,
    candidateEmail: string,
    startTime: string,
    endTime: string,
    subject?: string,
    body?: string
): Promise<{ success: boolean; joinUrl: string; event: any }> {
    const response = await fetch(`${API_BASE}/createMeeting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizerEmail, candidateName, candidateEmail, startTime, endTime, subject, body })
    });

    if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.details || errData.error || 'Failed to create meeting');
    }

    return await response.json();
}
